import { IContact } from "./Contact";
import { ILead } from "./Lead";
import { INote } from "./Note";
import { IPart, IPartCategory } from "./Part";
import { BaseEntity, DefaultResponseWithData } from "./system";
import { IVehicle } from "./Vehicle";

export interface IAllJobsResponse extends DefaultResponseWithData<IJob[]> { }
export interface ISingleJobResponse extends DefaultResponseWithData<IJob> { }
export interface IJobStatsResponse extends DefaultResponseWithData<JobStatsResponse> { }
export interface IPreviousJobPartListResponse extends DefaultResponseWithData<IPreviousJobPartList | null> { }
export interface IPreviousJobPartListsResponse extends DefaultResponseWithData<IPreviousJobPartList[]> { }
export interface IJobAssemblySessionResponse extends DefaultResponseWithData<IJobAssemblySession[]> { }

export interface JobStatsResponse {
    totalJobs: number;
    duration: JobTotalAvg;
    postageCost: JobTotalAvg;
    disCost: JobTotalAvg;
    whCost: JobTotalAvg;
    totalCost: JobTotalAvg;
    numberOfParts: JobTotalAvg;

    vehiclesAwaitingDismantling: number;
    partsAwaitingRemoval: number;
    partsRemovedToday: number;
}

export interface JobTotalAvg {
    total: number;
    average: number;
}

export interface CreateJobRequest {
    type: JobType;
    title: string;
    description: string;
    duration: number;
    estimatedPrice: number | null;
    estimatedMargin: number | null;
    leadId: string | null;
    vehicleId: string | undefined;

    jobPartCategories: {
        partCategory: IPartCategory;
        averagePrice: number;
        price: number;
        lastSoldPrice?: number | null;
        note?: string;
        coreOrderNumber?: string | null;
        accountId?: string | null;
    }[];
}

export interface CreateJobWithPartRequest {
    partId: string;
    jobId: string | null;
}

export interface IPreviousJobPartList {
    jobId: string;
    jobTitle: string;
    created: string;
    matchType: PreviousJobPartListMatchType;
    vehicleId: string;
    vrm: string | null;
    makeModelDescription: string | null;
    yearOfManufacture: number | null;
    categories: IPreviousJobPartListCategory[];
}

export interface IPreviousJobPartListCategory {
    partCategoryId: string;
    price: number | null;
    lastSoldPrice: number | null;
}

export enum PreviousJobPartListMatchType {
    KType,
    MakeModelYear,
    MakeModelYearRange
}

export interface IJob extends BaseEntity {
    type: JobType;
    title: string;
    description: string;
    duration: number;
    startDate: string | null;
    endDate: string | null;
    dismantlingStart: string | null;
    dismantlingEnd: string | null;

    estimatedPrice: number | null;
    estimatedMargin: number | null;

    completedOn: string | null;
    completedBy: string | null;
    deleted: string | null;
    deletedBy: string | null;
    leadId: string | null;
    lead: ILead | null;
    mechanicId: string | null;
    mechanic: IContact | null;
    jobPartCategories: JobPartCategory[];
    notes: INote[];

    //extra fields from DTO
    jobPartCount: number;
    awaitingDismantlingPartCount: number;
    collectedOn: string | null;
}

export interface JobPartCategory extends BaseEntity {
    jobId: string | null;
    job: IJob | null;
    partCategoryId: string | null;
    partCategory: IPartCategory | null;
    partDataId: string | null;
    partData: IPart | null;
}

export interface IJobAssemblySession extends BaseEntity {
    jobId: string | null;
    partAssemblyId: string | null;
    depollutionId: string | null;
    userId: string | null;
    dismantlingStart: string | null;
    dismantlingEnd: string | null;
    pauseReason: string | null;
}

export interface StartJobAssemblySessionRequest {
    jobId: string;
    partAssemblyId: string;
}

export interface PauseJobAssemblySessionRequest {
    jobId: string;
    partAssemblyId: string;
    userId: string;
    pauseReason: string;
}

export interface FinishJobAssemblySessionRequest {
    jobId: string;
    partAssemblyId: string;
    userId: string;
}

export enum JobType {
    Generic,
    Body,
    Mechanical
}